import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, Image, Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import error404 from 'assets/404_warning_icon.png';
import NoChartDataIcon from 'components/Icons/NoChartDataIcon';
import { useAuthContext } from 'context/authContext';
import { NewDimItemData } from 'models/response/blockOutput.interface';
import { STATUS } from 'projectConstants';
import {
  chartOutputStatus,
  getActiveTabIndex,
  getBlockDetails,
  getChartLabels,
  getChartType,
  setActiveTabIndexVersion2,
  setChartType,
} from 'redux/PlanPageSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

import Chart from '../../Plan/Chart';
import {
  selectActiveChartSeries,
  selectDefaultTabIndex,
  selectSafeActiveIndex,
} from '../planPageRevampSelectors';
import {
  PLAN_CARD_INNER_RADIUS,
  PLAN_CARD_PADDING,
  PLAN_CHART_VIEW_HEIGHT,
  PLAN_TABLE_CELL_BORDER,
  PLAN_TEXT,
  text as textToken,
} from '../planPageRevampUi';
import { buildChartImageFileName, downloadChartImage } from '../planChartDownload';

import '../planChartSlider.css';
import PlanCard from './PlanCard';
import PlanChartSkeleton from './PlanChartSkeleton';
import PlanChartToolbar from './PlanChartToolbar';

import type { ChartTypeRegistry } from 'chart.js';
import type { ChartJSOrUndefined } from 'react-chartjs-2/dist/types';

interface PlanChartViewProperties {
  chartType: string;
  chartData: NewDimItemData[];
  chartLabels: string[];
  tabData: unknown;
  activeTabIndex: number;
  onChartTypeChange: (value: string) => void;
  onDownload: () => void;
  isExporting: boolean;
  fillHeight?: boolean;
  chartAreaHeight?: string;
  chartExportReference?: React.MutableRefObject<ChartJSOrUndefined<keyof ChartTypeRegistry> | null>;
}

const PlanChartView = memo(
  ({
    chartType,
    chartData,
    chartLabels,
    tabData,
    activeTabIndex,
    onChartTypeChange,
    onDownload,
    isExporting,
    fillHeight = false,
    chartAreaHeight,
    chartExportReference,
  }: PlanChartViewProperties) => {
    const resolvedChartHeight = chartAreaHeight ?? PLAN_CHART_VIEW_HEIGHT;

    return (
      <>
        <PlanChartToolbar
          viewMode={chartType || 'bar'}
          onChange={onChartTypeChange}
          onDownload={onDownload}
          isExporting={isExporting}
        />

        {/* Figma 76685:6591/6592 — same p-16 + inner bordered-radius-8 box
            pattern the table card uses for its grid. */}
        <Box
          flex={fillHeight ? '1' : undefined}
          minH={fillHeight ? 0 : undefined}
          flexShrink={0}
          display='flex'
          flexDirection='column'
          w='100%'
          p={PLAN_CARD_PADDING}
          overflow={fillHeight ? 'hidden' : 'visible'}
        >
          <Box
            flex={fillHeight ? '1' : undefined}
            h={fillHeight ? undefined : resolvedChartHeight}
            minH={fillHeight ? 0 : resolvedChartHeight}
            border='1px solid'
            borderColor={PLAN_TABLE_CELL_BORDER}
            borderRadius={PLAN_CARD_INNER_RADIUS}
            p={3}
            overflow='hidden'
          >
            <Chart
              hideToolbar
              variant='revamp'
              chartData={chartData}
              chartLabels={chartLabels}
              tabData={tabData}
              activeTabIndex={activeTabIndex}
              exportRef={chartExportReference}
              plotHeight={fillHeight ? '100%' : resolvedChartHeight}
              fillPlotHeight={fillHeight}
            />
          </Box>
        </Box>
      </>
    );
  },
);

PlanChartView.displayName = 'PlanChartView';

const toChartData = (series: any): NewDimItemData[] =>
  series.dim_item_data ?? [{ value: series.total_values, name: 'Total' }];

interface PlanChartPanelProperties {
  fillHeight?: boolean;
  chartAreaHeight?: string;
  onToggleAuthModal: () => void;
}

const PlanChartPanel = ({
  fillHeight = false,
  chartAreaHeight,
  onToggleAuthModal,
}: PlanChartPanelProperties) => {
  const { user } = useAuthContext();
  const dispatch: AppDispatch = useDispatch();
  const chartExportReference = useRef<ChartJSOrUndefined<keyof ChartTypeRegistry> | null>(null);
  const activeSeries = useSelector(selectActiveChartSeries);
  const safeActiveIndex = useSelector(selectSafeActiveIndex);
  const defaultTabIndex = useSelector(selectDefaultTabIndex);
  const activeTabIndex = useSelector(getActiveTabIndex);
  const tabsDataLength = useSelector((state: any) => state.planPageSlice.chartOutput?.length ?? 0);
  const chartLabels = useSelector(getChartLabels);
  const chartStatus = useSelector(chartOutputStatus);
  const chartType = useSelector(getChartType);
  const blockDetails = useSelector(getBlockDetails);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (activeTabIndex === -1 && tabsDataLength > 0) {
      dispatch(setActiveTabIndexVersion2(defaultTabIndex));
    }
  }, [activeTabIndex, defaultTabIndex, dispatch, tabsDataLength]);

  const chartData = useMemo(() => (activeSeries ? toChartData(activeSeries) : []), [activeSeries]);

  const onChartTypeChange = useCallback(
    (value: string) => {
      dispatch(setChartType(value));
    },
    [dispatch],
  );

  const onDownload = useCallback(async () => {
    if (user?.is_anonymous) {
      onToggleAuthModal();
      return;
    }

    setIsExporting(true);
    try {
      const fileName = buildChartImageFileName(blockDetails?.name);
      downloadChartImage(chartExportReference.current, fileName);
    } finally {
      setIsExporting(false);
    }
  }, [blockDetails?.name, onToggleAuthModal, user?.is_anonymous]);

  const hasCachedChart = tabsDataLength > 0;

  const cardFlex = fillHeight ? '1 1 0' : undefined;
  const cardMinH = fillHeight ? 0 : undefined;

  if (
    (chartStatus === STATUS.LOADING && !hasCachedChart) ||
    (chartStatus === STATUS.IDLE && tabsDataLength === 0)
  ) {
    return (
      <PlanCard flex={cardFlex} minH={cardMinH}>
        <PlanChartSkeleton fillHeight={fillHeight} chartAreaHeight={chartAreaHeight} />
      </PlanCard>
    );
  }

  if (
    (tabsDataLength === 0 && chartStatus === STATUS.FAILED) ||
    (tabsDataLength === 0 && chartStatus === STATUS.SUCCESS)
  ) {
    return (
      <PlanCard flex={cardFlex} minH={cardMinH}>
        <Box
          w='100%'
          h={fillHeight ? '100%' : chartAreaHeight ?? PLAN_CHART_VIEW_HEIGHT}
          minH={fillHeight ? 0 : chartAreaHeight ?? PLAN_CHART_VIEW_HEIGHT}
          display='flex'
          justifyContent='center'
          alignItems='center'
        >
          {chartStatus === STATUS.SUCCESS && (
            <Flex direction='column' gap={2} alignItems='center'>
              <NoChartDataIcon width={28} height={28} color={PLAN_TABLE_CELL_BORDER} />
              <Text {...PLAN_TEXT.body} color={textToken.secondary}>
                No data to display for this indicator
              </Text>
            </Flex>
          )}
          {chartStatus === STATUS.FAILED && (
            <Flex direction='column' gap={2} alignItems='center'>
              <Image src={error404} alt='error icon' boxSize={40} />
              <Text {...PLAN_TEXT.body} color={textToken.secondary}>
                Something went wrong, Please contact support@blox.so
              </Text>
            </Flex>
          )}
        </Box>
      </PlanCard>
    );
  }

  if (!activeSeries || !chartLabels?.length) {
    return (
      <PlanCard flex={cardFlex} minH={cardMinH}>
        <PlanChartSkeleton fillHeight={fillHeight} chartAreaHeight={chartAreaHeight} />
      </PlanCard>
    );
  }

  return (
    <PlanCard flex={cardFlex} minH={cardMinH}>
      <PlanChartView
        chartType={chartType}
        chartData={chartData}
        chartLabels={chartLabels}
        tabData={activeSeries}
        activeTabIndex={safeActiveIndex}
        onChartTypeChange={onChartTypeChange}
        onDownload={onDownload}
        isExporting={isExporting}
        fillHeight={fillHeight}
        chartAreaHeight={chartAreaHeight}
        chartExportReference={chartExportReference}
      />
    </PlanCard>
  );
};

export default memo(PlanChartPanel);
